import React from 'react';
import { useAuth } from '../context/AuthContext';

const NAV_MAIN = [
  { id: 'home',          label: 'Home',          icon: '⊞' },
  { id: 'leads',         label: 'Leads',         icon: '○', badge: 'leads' },
  { id: 'opportunities', label: 'Opportunities',  icon: '◈', badge: 'opps' },
  { id: 'clients',       label: 'Clients',        icon: '◆' },
  { id: 'activities',    label: 'Activities',     icon: '▶' },
  { id: 'contacts',      label: 'Contacts',       icon: '◆' },
];

const NAV_INTEL = [
  { id: 'kpis',     label: 'KPI Dashboard', icon: '◇' },
];

const NAV_TOOLS = [
  { id: 'tools',    label: 'Tools Hub',  icon: '□' },
];

export default function Sidebar({ page, onNavigate, data = {}, profile }) {
  const { signOut } = useAuth();

  const { leads = [], opportunities = [], clients = [] } = data;

  const name = profile?.name || '';
  const firm = profile?.firm_name || '';
  const firstName = name.split(' ')[0] || 'You';
  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'U';

  // Revenue progress for sidebar bar
  const annualGoal = profile?.annual_goal || 1000000;
  const activeClients = clients.filter(c => !c.archived);
  const contracted = activeClients.reduce((s, c) => s + (c.contract_value || 0), 0);
  const revPct = Math.min(Math.round((contracted / annualGoal) * 100), 100);

  function fmt(n) {
    if (!n) return '$0';
    if (n >= 1000000) return `$${(n/1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${Math.round(n/1000)}K`;
    return `$${n}`;
  }

  // Badge counts
  const leadsNoNext = leads.filter(l => !l.archived && !l.next_step).length;
  const activeOpps = opportunities.filter(o => o.stage !== 'Closed Won' && o.stage !== 'Closed Lost' && !o.archived);
  const oppBadge = activeOpps.length;

  function getBadge(id) {
    if (id === 'leads') return leadsNoNext > 0 ? { count: leadsNoNext, cls: 'badge-gold' } : null;
    if (id === 'opps') return oppBadge > 0 ? { count: oppBadge, cls: 'badge-teal' } : null;
    return null;
  }

  const NavItem = ({ item }) => {
    const badge = item.badge ? getBadge(item.badge) : null;
    const isActive = page === item.id;
    return (
      <div
        className={`sb-item${isActive ? ' active' : ''}`}
        onClick={() => onNavigate(item.id)}
      >
        <span className="sb-icon">{item.icon}</span>
        <span>{item.label}</span>
        {badge && (
          <span className={`sb-badge ${badge.cls}`}>{badge.count}</span>
        )}
      </div>
    );
  };

  return (
    <nav className="sidebar">
      {/* Brand */}
      <div className="sb-brand">
        <div className="sb-logo-label">Stimulyst</div>
        <div className="sb-product">doer-seller</div>
        <div className="sb-tagline">Corporate Client CRM</div>
      </div>

      {/* User */}
      <div className="sb-user">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--teal)', color: '#fff',
            fontSize: 11, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <div className="sb-user-name">{firstName}</div>
            <div className="sb-user-co">{firm}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div className="sb-nav">
        <div className="sb-section">Main</div>
        {NAV_MAIN.map(item => <NavItem key={item.id} item={item} />)}

        <div className="sb-section">Intelligence</div>
        {NAV_INTEL.map(item => <NavItem key={item.id} item={item} />)}

        <div className="sb-section">Tools</div>
        {NAV_TOOLS.map(item => <NavItem key={item.id} item={item} />)}

        <div className="sb-divider" />

        <div
          className={`sb-item${page === 'settings' ? ' active' : ''}`}
          onClick={() => onNavigate('settings')}
        >
          <span className="sb-icon">⚙</span>
          <span>Settings</span>
        </div>
      </div>

      {/* Revenue Progress Bar — matches v59 .sb-progress */}
      <div className="sb-progress">
        <div className="sb-prog-label">
          <span>Revenue Progress</span>
          <span>{revPct}%</span>
        </div>
        <div className="sb-prog-bar">
          <div className="sb-prog-fill" style={{ width: `${revPct}%` }} />
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.16)', marginTop: 3 }}>
          {fmt(contracted)} of {fmt(annualGoal)} — keep going!
        </div>
      </div>

      {/* Footer */}
      <div className="sb-footer">
        <span
          className="sb-footer-txt"
          style={{ cursor: 'pointer' }}
          onClick={signOut}
        >
          Sign out · Stimulyst © 2026
        </span>
        <span style={{ fontSize: 10, color: 'rgba(93,219,130,0.55)', fontWeight: 600 }}>● Saved</span>
      </div>
    </nav>
  );
}
